import { ThemeProvider, Box } from '@mui/material';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import { Document } from 'src/components/Document';
import { ErrorBoundary } from 'react-error-boundary';
import CssBaseline from '@mui/material/CssBaseline';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { LicenseInfo } from '@mui/x-license-pro';
import { RolesRestricted } from '../RequiredAuth/RolesAuthRoute';
import { ConfiguredOktaSecurity } from 'src/contexts/ConfiguredOktaSecurity';
import { ConfiguredApolloProvider } from 'src/contexts/ConfiguredApolloProvider';
import { AuthContextProvider } from 'src/contexts/AuthContext';
import { BrowseContextProvider } from 'src/contexts/BrowseContext';
import './App.css';
import { MyCPD } from 'src/components/Pages/MyCPD';
import { BrowsePage } from 'src/components/Pages/Browse';
import { HistoryPage } from 'src/components/Pages/History';
import { ActivityPage } from 'src/components/Pages/Activity';
import { PdpPage } from 'src/components/Pages/PDP';
import { HlrPage } from 'src/components/Pages/HLR';
import { ALSForm as ALSForm, EditALSForm as EditALSForm } from 'src/components/Pages/ALS';
import { CPRForm as CPRForm, EditCPRForm as EditCPRForm } from 'src/components/Pages/CPR';
import { GPAForm as GPAForm, EditGPAForm as EditGPAForm } from 'src/components/Pages/GPA';
import { ActivitiesInProgress } from '../Pages/ActivitiesInProgress';
import { BookmarksPage } from 'src/components/Pages/Bookmarks';
import { HelpPage } from 'src/components/Pages/Help';
import { PreferencesPage } from 'src/components/Pages/Preferences';
import { NotificationsPage } from 'src/components/Pages/Notifications';
import { ContactUsPage } from 'src/components/Pages/ContactUs';
import { ResourcesPage } from 'src/components/Pages/Resources';
import { GuidesAndTemplatesPage } from 'src/components/Pages/GuidesAndTemplates';
import { MajorCPDProvidersPage } from 'src/components/Pages/MajorCPDProviders';
import { RequirementsPage } from 'src/components/Pages/Requirements';
import { MissingPage } from '../Error/MissingPage';
import Header from 'src/components/Header/Header';
import Footer from 'src/components/Footer/Footer';
import { theme } from './theme';
import { ErrorBoundaryFallback } from 'src/components/ErrorBoundary/ErrorBoundaryFallback';
import { EditInterests } from '../Pages/EditInterests';
import {
  EditQuickLog as EditQuickLogPage,
  NewQuickLog as NewQuickLogPage,
  EditQuickLogByCpdId as EditQuickLogByCpdIdPage
} from '../Pages/Log';
import { HistoryContextProvider } from '../../contexts/HistoryContext';
import { PageErrorBoundary } from '../ErrorBoundary/PageErrorBoundary';
import { ScrollToTop } from 'src/components/ScrollToTop/ScrollToTop';
import { WhatsNewPopup } from 'src/components/WhatsNewPopup/WhatsNewPopup';
import { Chatbot } from 'src/components/Chatbot/Chatbot';

LicenseInfo.setLicenseKey(
  '3e81fbc23b8e627614057a18da188ed5Tz0xMDU4NzAsRT0xNzY4NDM1MTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI='
);

function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <ScrollToTop />
          <ConfiguredOktaSecurity>
            <ConfiguredApolloProvider>
              <AuthContextProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <BrowseContextProvider>
                    <Box
                      sx={{
                        height: '100vh',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <WhatsNewPopup />
                      <Header />

                      <Box
                        sx={{
                          backgroundColor: '#f8f8f8',
                          flexGrow: 1,
                          pb: 7
                        }}
                      >
                        <Switch>
                          <Route
                            path="/"
                            exact
                            component={() => (
                              <PageErrorBoundary key={'mycpd-boundary'}>
                                <RolesRestricted
                                  roles={['member', 'staff']}
                                  fallbackRoute="/browse"
                                >
                                  <Document title="MyCPD - Home">
                                    <MyCPD />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/mycpd"
                            component={() => (
                              <PageErrorBoundary key={'mycpd-boundary'}>
                                <RolesRestricted
                                  roles={['member', 'staff']}
                                  fallbackRoute="/browse"
                                >
                                  <Document title="MyCPD - Home">
                                    <MyCPD />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/edit-interests"
                            component={() => (
                              <PageErrorBoundary key={'edit-interests-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Edit Interests">
                                    <EditInterests />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path={'/log/cpdid/:cpdId'}
                            component={() => (
                              <PageErrorBoundary key={'log-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Edit Quick Log By Id">
                                    <EditQuickLogByCpdIdPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path={'/log/:id'}
                            component={() => (
                              <PageErrorBoundary key={'log-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Edit Quick Log">
                                    <EditQuickLogPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/log"
                            component={() => (
                              <PageErrorBoundary key={'log-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - New Quick Log">
                                    <NewQuickLogPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/als/cpdid/:cpdId"
                            component={() => (
                              <PageErrorBoundary key={'als-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Advanced life support">
                                    <EditALSForm />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/als/:activitykey"
                            component={() => (
                              <PageErrorBoundary key={'als-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Advanced life support">
                                    <EditALSForm />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/als"
                            component={({ location }: RouteComponentProps) => (
                              <PageErrorBoundary key={'als-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Advanced life support">
                                    <ALSForm key={location.key} />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/cpr/cpdid/:cpdId"
                            component={() => (
                              <PageErrorBoundary key={'cpr-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Cardiopulmonary Resuscitation">
                                    <EditCPRForm />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/cpr/:activitykey"
                            component={() => (
                              <PageErrorBoundary key={'cpr-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Cardiopulmonary Resuscitation">
                                    <EditCPRForm />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/cpr"
                            component={({ location }: RouteComponentProps) => (
                              <PageErrorBoundary key={'cpr-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Cardiopulmonary Resuscitation">
                                    <CPRForm key={location.key} />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />

                          <Route
                            path="/gpl/cpdid/:cpdId"
                            component={() => (
                              <PageErrorBoundary key={'gpa-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Group or practice activity">
                                    <EditGPAForm />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/gpl/:activitykey"
                            component={() => (
                              <PageErrorBoundary key={'gpa-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Group or practice activity">
                                    <EditGPAForm />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/gpl"
                            component={({ location }: RouteComponentProps) => (
                              <PageErrorBoundary key={'gpa-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Group or practice activity">
                                    <GPAForm key={location.key} />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/activities-in-progress"
                            component={() => (
                              <PageErrorBoundary key={'aip-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Activities In Progress">
                                    <ActivitiesInProgress />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/browse"
                            component={() => (
                              <PageErrorBoundary key={'browse-boundary'}>
                                <RolesRestricted roles={['member', 'staff', 'provider']}>
                                  <Document title="MyCPD - Browse">
                                    <BrowsePage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/activity/:id"
                            component={() => (
                              <PageErrorBoundary key={'activity'}>
                                <RolesRestricted roles={['member', 'staff', 'provider']}>
                                  <Document title="MyCPD - Activity">
                                    <ActivityPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/history"
                            component={() => (
                              <PageErrorBoundary key={'history-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <HistoryContextProvider>
                                    <Document title="MyCPD - History">
                                      <HistoryPage />
                                    </Document>
                                  </HistoryContextProvider>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/pdp/:year"
                            component={() => (
                              <PageErrorBoundary key={'pdp-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - PDP">
                                    <PdpPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/hlr/:activitykey"
                            component={() => (
                              <PageErrorBoundary key={'pdp-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - High Level Requirements">
                                    <HlrPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/bookmarks"
                            component={() => (
                              <PageErrorBoundary key={'bookmarks-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Bookmarks">
                                    <BookmarksPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/help"
                            component={() => (
                              <PageErrorBoundary key={'help-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Help">
                                    <HelpPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/preferences"
                            component={() => (
                              <PageErrorBoundary key={'preferences-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Preferences">
                                    <PreferencesPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/notifications"
                            component={() => (
                              <PageErrorBoundary key={'notifications-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Notifications">
                                    <NotificationsPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/contact-us"
                            component={() => (
                              <PageErrorBoundary key={'contact-us-boundary'}>
                                <RolesRestricted roles={['member', 'staff', 'provider']}>
                                  <Document title="MyCPD - Contact Us">
                                    <ContactUsPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/resources"
                            component={() => (
                              <PageErrorBoundary key={'resources-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Resources">
                                    <ResourcesPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/guides-templates"
                            component={() => (
                              <PageErrorBoundary key={'guides-templates-boundary'}>
                                <RolesRestricted roles={['member', 'staff']}>
                                  <Document title="MyCPD - Guides and Templates">
                                    <GuidesAndTemplatesPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/major-cpd-providers"
                            component={() => (
                              <PageErrorBoundary key={'major-cpd-providers'}>
                                <RolesRestricted roles={['member', 'staff', 'provider']}>
                                  <Document title="MyCPD - Major CPD Providers">
                                    <MajorCPDProvidersPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          <Route
                            path="/requirements"
                            component={() => (
                              <PageErrorBoundary key={'requirements'}>
                                <RolesRestricted roles={['staff']}>
                                  <Document title="MyCPD - Requirements">
                                    <RequirementsPage />
                                  </Document>
                                </RolesRestricted>
                              </PageErrorBoundary>
                            )}
                          />
                          {/* Note: MissingPage as last Route so it is default if nothing else matches */}
                          <Route
                            path="*"
                            component={() => (
                              <PageErrorBoundary key={'missing-page-boundary'}>
                                <Document title="MyCPD - Page not found">
                                  <MissingPage />
                                </Document>
                              </PageErrorBoundary>
                            )}
                          />
                        </Switch>
                      </Box>

                      <RolesRestricted
                        roles={['staff', 'member']}
                        isPilotRestricted={true}
                        hideChildComponent={true}
                      >
                        <Chatbot />
                      </RolesRestricted>
                      <Footer />
                    </Box>
                  </BrowseContextProvider>
                </LocalizationProvider>
              </AuthContextProvider>
            </ConfiguredApolloProvider>
          </ConfiguredOktaSecurity>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
